/**
 * Operational hierarchy rows — spec §F5.8 (widget + boundary symbology) and §D2
 * (`/hierarchy/units?type=…&level=…&country=…&simplify=…`).
 *
 * One table drives three consumers: the widget rows (§F5.8), the Leaflet layers
 * (`HierarchyLayers.tsx`) and the `N layers on` count in the status bar (§F5.10) — so a
 * row can never be visible in one place and missing in another.
 *
 * **State placement decision:** which rows are ticked is *map-view* state, not part of the
 * §F1 `AppState` interface (which the spec fixes verbatim and which has no field for it).
 * It therefore lives in its own small zustand slice here rather than in `lib/state.ts`;
 * the reporting-level radios still write `reportingLevel` in the §F1 store.
 */
import { create } from 'zustand';
import type L from 'leaflet';

import { PARTNERSHIP_LEVEL, WATERSHED_LEVEL, type Country, type ReportingLevel, type Scope } from '../../lib/state';
import type { ReferenceLayerConfig, Simplify, UnitType } from '../../lib/api';
import { patternUrl, type HatchSpec } from '../../lib/svgPattern';

export type HierarchyKey = 'adm0' | 'adm1' | 'adm2' | 'wsheds' | 'partners';

/**
 * How a row paints its interior — client gripe, 2026-09-03: watershed and partnership
 * boundaries "clash" whenever a §F5.8 watershed filter puts both on the map at once.
 *
 * They clashed because they were the same mark twice: two dashed outlines over two flat
 * 6–7 % washes, so nothing said which line went with which hierarchy and the washes
 * multiplied into a third colour. The two are now different *kinds* of mark:
 *
 * * `'tint'` — an **area**. A flat, very low-opacity fill from the row's own colour family
 *   under a firm solid outline. This is the watershed: a region of the map.
 * * `'hatch'` — an **overlay**. Diagonal lines on a transparent ground (`lib/svgPattern.ts`)
 *   under a thin solid outline. This is the partnership: something laid *over* the map, and
 *   the transparency between its lines is what lets the watershed tint (or a choropleth)
 *   still read underneath it.
 *
 * Absent (the three admin rows) means `fill: false` — a line and nothing else.
 */
export type HierarchyFill = 'tint' | 'hatch';

/**
 * The partnership hatch. Plum, the family §F5.8 has always drawn partnerships in.
 *
 * 7 px spacing at 1.3 px is deliberately open: the point of the hatch is that what is under
 * it stays readable, and a tighter one reads as a solid wash again — the thing this replaced.
 */
export const PARTNERSHIP_HATCH: HatchSpec = {
  id: 'svp-hatch-partnership',
  stroke: '#8E3B62',
  spacing: 7,
  width: 1.3,
  opacity: 0.55,
  angle: 45,
};

export interface HierarchyRow {
  key: HierarchyKey;
  /** i18n key under `hierarchy.rows.` (§F12: every string through `t()`). */
  labelKey: string;
  /** Fallback label — also the text used in the disabled-radio tooltip. */
  label: string;
  type: UnitType;
  /** §D2: `level` is only accepted with `type=admin`. */
  level: number | null;
  /**
   * Reporting level this row sets (§F5.8); null = radio permanently disabled.
   *
   * The Watersheds row is `'watershed'` since §M3.5: MELS reports B.2.1/C.2.1 per
   * `watershed_id`, so the radio is live **for the indicators that have those values**.
   * "Enabled" is therefore a per-indicator question the widget asks (`has_watershed_values`)
   * rather than a property of the row — Partnerships stay `null`, which is the row-level
   * "not until the advanced-analysis phase".
   */
  reportingLevel: ReportingLevel | null;
  source: string;
  simplify: Simplify;
  style: L.PathOptions;
  /** Area treatment, if this row has one — see `HierarchyFill` and `rowStyle`. */
  fill?: HierarchyFill;
  /** Tooltip text prefix — watersheds read `WS {name}` per §F5.8. */
  tooltipPrefix?: string;
  /** True for rows too heavy to draw programme-wide (communes, §D2/§J1 payload budget). */
  zoomGated?: boolean;
}

/** §F5.8 row order: Countries, Regions (ADM1), Communes (ADM2), Watersheds, Partnerships. */
export const HIERARCHY_ROWS: readonly HierarchyRow[] = [
  {
    key: 'adm0',
    labelKey: 'hierarchy.rows.adm0',
    label: 'Countries',
    type: 'admin',
    level: 0,
    reportingLevel: 0,
    source: 'IFDC shapefile',
    simplify: 'z7',
    style: { color: '#22301F', weight: 2.2, opacity: 0.85, fill: false },
  },
  {
    key: 'adm1',
    labelKey: 'hierarchy.rows.adm1',
    label: 'Regions (ADM1)',
    type: 'admin',
    level: 1,
    reportingLevel: 1,
    source: 'IFDC shapefile',
    simplify: 'z7',
    style: { color: '#55614F', weight: 1, opacity: 0.7, dashArray: '3,3', fill: false },
  },
  {
    key: 'adm2',
    labelKey: 'hierarchy.rows.adm2',
    label: 'Communes (ADM2)',
    type: 'admin',
    level: 2,
    reportingLevel: 2,
    source: 'IFDC shapefile',
    simplify: 'z7',
    style: { color: '#8B958A', weight: 0.8, opacity: 0.7, dashArray: '2,3', fill: false },
    zoomGated: true,
  },
  {
    key: 'wsheds',
    labelKey: 'hierarchy.rows.wsheds',
    label: 'Watersheds',
    type: 'watershed',
    level: null,
    reportingLevel: 'watershed',
    source: 'IFDC shapefile',
    simplify: 'z7',
    // An **area**: a barely-there blue wash under a firm, darker, solid outline. The dash is
    // gone on purpose — a dashed edge reads as "provisional", and a watershed is the most
    // definite polygon on this map. `#1D4F7C` is the same blue two steps down, so the outline
    // reads as the edge of its own fill rather than as a second colour.
    style: {
      color: '#1D4F7C',
      weight: 1.8,
      opacity: 0.9,
      fillColor: '#2B6CB0',
      fillOpacity: 0.1,
    },
    fill: 'tint',
    tooltipPrefix: 'WS ',
  },
  {
    key: 'partners',
    labelKey: 'hierarchy.rows.partners',
    label: 'Partnerships',
    type: 'partnership',
    level: null,
    // Live since `/values?level=partnership` landed. It was `null` — a permanently dead
    // radio — only because §D3 had no whole-level form for this hierarchy, not because the
    // engine could not value it: §E4 has always area-weighted partnerships from their
    // `unit_overlaps` cells, and `?partnership_id=` served them one at a time.
    reportingLevel: 'partnership',
    source: 'IFDC shapefile',
    simplify: 'z7',
    // An **overlay**: the plum hatch over whatever is underneath, held by a thin solid line.
    // `fillColor` is a paint-server reference, which is legal anywhere a colour is and is why
    // no Leaflet plugin was needed — `HierarchyLayers` registers the def on the renderer.
    // `fillOpacity: 1` because the alpha that matters is the hatch line's own
    // (`PARTNERSHIP_HATCH.opacity`); the ground between the lines is transparent regardless.
    style: {
      color: '#8E3B62',
      weight: 1.1,
      opacity: 0.95,
      fillColor: patternUrl(PARTNERSHIP_HATCH.id),
      fillOpacity: 1,
    },
    fill: 'hatch',
  },
];

/**
 * **One symbol colour for the operational hierarchy** — client change, 23 September 2026.
 *
 * > *"we should have a single symbol colour for the operational hierarchy."*
 *
 * Under **All** the map carries no data at all (`applyThemeDefault`), so the five rows are no
 * longer five things a reader has to tell apart — they are one drawing of where the programme
 * works. Five colours, three dash rhythms, a tint and a hatch over an empty map is a legend
 * for a question nobody asked.
 *
 * So every drawn row takes this: the brand green as a hairline, over the faintest wash of
 * itself. The wash is there and not `fill: false` because the hover card is on the polygon —
 * a filled shape is the hit area, and a reader pointing *inside* a region has to get that
 * region's card rather than having to find its 1.2 px edge.
 */
/**
 * The hover highlight (client, 2026-09-23: *"some nice hover effect like highlight when a
 * user moves over an operational hierarchy"*). Under All the flat green deepens and the
 * outline thickens; under every other theme the area stays as it is (a choropleth may be
 * painting it) and only the outline steps up, in the brand green, so the unit under the
 * pointer reads as *this one* without lying about its colour.
 */
export const HOVER_UNIFORM_STYLE: L.PathOptions = {
  color: '#1B5E20',
  weight: 2.4,
  opacity: 1,
  fillColor: '#7FB779',
  fillOpacity: 1,
};
export const HOVER_OUTLINE_STYLE: L.PathOptions = { color: '#2E7D32', weight: 2.4, opacity: 1 };

export function hoverStyle(uniform: boolean): L.PathOptions {
  return uniform ? HOVER_UNIFORM_STYLE : HOVER_OUTLINE_STYLE;
}

export const UNIFORM_ROW_STYLE: L.PathOptions = {
  color: '#2E7D32',
  weight: 1.2,
  opacity: 0.95,
  fill: true,
  // Solid, not a tint (client, 2026-09-23: "the colour should not be transparent"): under
  // All the hierarchy *is* the map, so its area is painted as one flat symbol colour.
  fillColor: '#A9CFA4',
  fillOpacity: 1,
};

/**
 * The style this row draws with *right now* — `row.style`, minus any fill the choropleth has
 * taken over.
 *
 * **Fills belong to the choropleth.** It is the layer answering a question the reader asked;
 * a boundary tint over it is a colour cast on every class of the ramp, and the boundary loses
 * nothing by keeping only its outline. So the watershed's tint yields to *any* active
 * choropleth, at any level.
 *
 * **The hatch does not**, and that asymmetry is the whole design. It is an overlay — lines
 * with transparent ground — so it stays legible over a choropleth and the choropleth stays
 * legible under it. The one case where it yields is the choropleth painting *its own*
 * polygons (`level=partnership`): there the hatch would sit exactly on top of the fill it is
 * meant to be describing and hide the colour, so the partnership keeps its outline and lets
 * the data show. The three admin rows are `fill: false` already, so this is a no-op for them.
 *
 * `choroLevel` is the level the fill is painting at, or `null` when nothing is mapped.
 *
 * `uniform` is the client's 23 September rule: **All** empties the map, and the hierarchy it
 * leaves behind is drawn as one thing rather than as five. See `UNIFORM_ROW_STYLE`.
 */
export function rowStyle(
  row: HierarchyRow,
  choroLevel: ReportingLevel | null,
  uniform = false,
): L.PathOptions {
  if (uniform) return UNIFORM_ROW_STYLE;
  if (!row.fill || choroLevel === null) return row.style;
  const yields = row.fill === 'tint' || choroLevel === row.reportingLevel;
  return yields ? { ...row.style, fill: false } : row.style;
}

/**
 * Communes are ~1 050 features programme-wide (1.1 MB at `simplify=z7`), so they are only
 * fetched when the view is already local: zoom ≥ 7 **or** a single country selected.
 */
export const COMMUNE_MIN_ZOOM = 7;

export function communesUnlocked(zoom: number, country: Country, scoped = false): boolean {
  // A §F5.8 scope opens the gate whatever the zoom: the gate exists because *programme-wide*
  // ADM2 is 1 050 features, and the whole point of the filter is that the request is now for
  // four of them. Keeping it shut under a scope would hide the one layer the reader filtered
  // to and blame the zoom for it.
  return scoped || zoom >= COMMUNE_MIN_ZOOM || country !== 'ALL';
}

/**
 * The §F5.8 rows a scope leaves on the map — `null` when there is no filter (all five).
 *
 * "All other layers should disappear except the filtered", read literally for boundaries:
 * what stays is the scope's **own outline** and its **children's**, which is exactly the
 * pair the choropleth is drawing between. A region scope keeps Regions (that one region's
 * outline, via `?parent=`) and Communes (its four); a watershed keeps Watersheds and
 * Partnerships. Nothing above the scope survives — a country border around a single commune
 * is not context, it is the unfiltered map still showing through.
 */
export function scopedRows(scope: Scope | null): readonly HierarchyKey[] | null {
  if (!scope) return null;
  if (scope.unitType === WATERSHED_LEVEL) return ['wsheds', 'partners'];
  if (scope.unitType === PARTNERSHIP_LEVEL) return ['partners'];
  if (scope.adminLevel === 0) return ['adm0', 'adm1', 'adm2'];
  if (scope.adminLevel === 1) return ['adm1', 'adm2'];
  return ['adm2'];
}

/** Is this row drawable under the current filter? True for every row when unscoped. */
export function rowInScope(key: HierarchyKey, scope: Scope | null): boolean {
  const allowed = scopedRows(scope);
  return allowed === null || allowed.includes(key);
}

/**
 * The visibility a scope change installs: exactly the rows it allows, and nothing else.
 *
 * This *writes* the §F5.8 checkboxes rather than merely overriding what they draw, because
 * the two would then disagree — a ticked "Countries" row over a map with no country outline
 * on it is a control lying about the map. The reader can still untick an allowed row; what
 * they cannot do while filtered is tick a row outside the filter (the widget disables it).
 */
export function visibilityForScope(scope: Scope | null): Record<HierarchyKey, boolean> {
  const allowed = scopedRows(scope);
  if (allowed === null) return { ...DEFAULT_VISIBLE };
  return Object.fromEntries(
    (Object.keys(DEFAULT_VISIBLE) as HierarchyKey[]).map((key) => [key, allowed.includes(key)]),
  ) as Record<HierarchyKey, boolean>;
}

/** §F5.8 defaults: Countries ✓, Regions ✓, the rest off. */
export const DEFAULT_VISIBLE: Record<HierarchyKey, boolean> = {
  adm0: true,
  adm1: true,
  adm2: false,
  wsheds: false,
  partners: false,
};

/** Do these two row sets say the same thing? Cheap, and there are exactly five rows. */
function sameRows(
  a: Record<HierarchyKey, boolean>,
  b: Record<HierarchyKey, boolean>,
): boolean {
  return HIERARCHY_ROWS.every((r) => a[r.key] === b[r.key]);
}

interface HierarchyState {
  visible: Record<HierarchyKey, boolean>;
  /**
   * The rows the reader had ticked before a §F5.8 filter overwrote them — `null` when
   * unfiltered. See `applyScope`.
   */
  beforeScope: Record<HierarchyKey, boolean> | null;
  /**
   * The one writer for a reader's own tick — C1's `LayerManager` checkbox.
   *
   * There was a `toggle(key)` beside this until C1's follow-up deleted it. It had had no
   * caller since R5 removed the "View data by:" rows, and once the checkbox came back it still
   * had none: a controlled `<input type="checkbox">` already knows what it was moved *to*
   * (`e.target.checked`), and flipping the stored value instead would disagree with the box
   * the moment anything else wrote `visible` in the same tick — which `applyScope` does. Two
   * writers for one field, one of them dead, was the smell NOTES #496 was written about.
   */
  setVisible: (key: HierarchyKey, on: boolean) => void;
  /**
   * §F5.8: install the rows a scope implies, and give the reader's own back when it clears.
   *
   * A filter *borrows* these five checkboxes — it has to, or a ticked "Countries" row would
   * sit over a map with no country outline on it (`visibilityForScope`). Borrowing means
   * giving back: the reporting level is already restored across the same round trip
   * (`lastAdminLevel`), and a reader who ticked Watersheds and unticked Countries, filtered
   * to a region and then cleared was getting the *defaults* handed back instead of their own
   * map. Only the first scope remembers — walking region → commune → watershed is one
   * filtering session, and the thing to return to is what came before all of it.
   */
  applyScope: (scope: Scope | null) => void;
  reset: () => void;
}

export const useHierarchyStore = create<HierarchyState>((set) => ({
  visible: { ...DEFAULT_VISIBLE },
  beforeScope: null,
  setVisible: (key, on) => set((s) => ({ visible: { ...s.visible, [key]: on } })),
  // No-ops when nothing moves: this runs on every scope change including the boot one
  // (`null` → `null`), and a new object each time would re-render every row for nothing.
  applyScope: (scope) =>
    set((s) => {
      if (!scope) {
        // Nothing was borrowed (the boot call, `null` → `null`) — leave the rows alone.
        if (!s.beforeScope) return {};
        return sameRows(s.visible, s.beforeScope)
          ? { beforeScope: null }
          : { visible: s.beforeScope, beforeScope: null };
      }
      const wanted = visibilityForScope(scope);
      return {
        // `{...s.visible}` because `visible` is about to be replaced and the remembered copy
        // must not be the object the store keeps mutating through it.
        beforeScope: s.beforeScope ?? { ...s.visible },
        ...(sameRows(s.visible, wanted) ? {} : { visible: wanted }),
      };
    }),
  reset: () => set({ visible: { ...DEFAULT_VISIBLE }, beforeScope: null }),
}));

/**
 * Is this row actually drawn right now? A ticked row still renders nothing while its gate
 * is closed (communes at scope ALL below zoom 7), so this is the single definition of
 * "on the map" shared by `HierarchyLayers` and the §F5.10 layer count.
 */
export function rowActive(
  row: HierarchyRow,
  visible: Record<HierarchyKey, boolean>,
  zoom: number,
  country: Country,
  scope: Scope | null = null,
): boolean {
  return (
    visible[row.key] &&
    rowInScope(row.key, scope) &&
    (!row.zoomGated || communesUnlocked(zoom, country, scope !== null))
  );
}

/** §F5.10 `N layers on` — in M1 the only map layers are the hierarchy rows. */
export function countVisible(
  visible: Record<HierarchyKey, boolean>,
  zoom: number,
  country: Country,
  scope: Scope | null = null,
): number {
  return HIERARCHY_ROWS.filter((r) => rowActive(r, visible, zoom, country, scope)).length;
}

/**
 * Which §F5.8 row a config entry is about — matched on what it *draws*, not on its name.
 *
 * `key` is the server's word ("countries", "watersheds", "partnerships") and `HierarchyKey`
 * is the mockup's ("adm0", "wsheds", "partners"); a lookup table between two vocabularies is
 * a third thing to keep in step. `unit_type` + `admin_level` is the pair §D2 actually asks
 * for, and both sides already carry it.
 */
export function referenceRowKey(entry: ReferenceLayerConfig): HierarchyKey | null {
  const row = HIERARCHY_ROWS.find(
    (r) => r.type === entry.unit_type && r.level === entry.admin_level,
  );
  return row?.key ?? null;
}

/* ------------------------------------------------- permanent unit labels (zoom-in) */

/**
 * The zoom a country's name becomes a permanent label on the map.
 *
 * 5 is the first zoom at which the four programme countries are wider than the word
 * written across them — below it the labels are four names stacked over one blob, which
 * is a legend rendered badly rather than a map.
 */
export const COUNTRY_LABEL_MIN_ZOOM = 5;

/**
 * The same for a watershed. Two steps deeper than a country because there are an order of
 * magnitude more of them and they are correspondingly smaller: at 5 they overprint each
 * other, at 7 each one has room for its own name.
 */
export const WATERSHED_LABEL_MIN_ZOOM = 7;

/**
 * The zoom from which units at `level` carry a permanent centroid label, or `null` for a
 * level that never does.
 *
 * Only countries and watersheds are labelled. Regions and communes are not: at the zooms
 * where they are legible the reader is already hovering them (`unit-tip`), and ~1 050
 * commune names written permanently is not a map. Partnerships are not either — they are
 * an overlay (`HierarchyFill`), and the point of an overlay is that what is under it stays
 * readable.
 */
export function labelMinZoom(level: ReportingLevel | null): number | null {
  if (level === 0) return COUNTRY_LABEL_MIN_ZOOM;
  if (level === WATERSHED_LEVEL) return WATERSHED_LABEL_MIN_ZOOM;
  return null;
}

/** Are `level`'s names shown at this zoom? The one threshold both label sites ask. */
export function labelsAtZoom(level: ReportingLevel | null, zoom: number): boolean {
  const min = labelMinZoom(level);
  if (min === null || zoom < min) return false;
  // NOTES #738 (e2e read): once the watersheds are named, the country names stand down —
  // fifteen labels in one frame read as clutter, and a country name beside a basemap
  // capital reads as a city.
  if (level === 0 && zoom >= WATERSHED_LABEL_MIN_ZOOM) return false;
  return true;
}

/**
 * A result layer as the label rule sees it — its id, and the little of its spec that
 * decides whether it owns the names. Spelled structurally rather than imported from
 * `lib/state`, so this module stays free of the §F6 result types.
 */
export interface LabelCandidate {
  id: string;
  spec: {
    kind: string;
    unit_ids?: readonly string[] | null;
    values?: Record<string, number> | null;
    outlineOnly?: boolean;
  };
}

/**
 * The §F6 layers a use case draws to say *this one* — `intel:{uc}:selected`, built in
 * `useUnitLink.ts`. One unit, outline only, and never the owner of a level's names: it
 * covers a single watershed, so handing it the labels would silence every other name on
 * the map to write the one the reader has already clicked.
 */
const SELECTED_HIGHLIGHT = /^intel:[^:]+:selected$/;

/** How many org units a spec names — `unit_ids` for a `units` layer, the keys of `values`. */
function labelUnitCount(spec: LabelCandidate['spec']): number {
  if (spec.kind === 'units') return spec.unit_ids?.length ?? 0;
  if (spec.kind === 'choropleth') return Object.keys(spec.values ?? {}).length;
  return 0;
}

/**
 * **The one predicate both label sites ask**: which result layer, if any, writes the unit
 * names right now.
 *
 * Two rules, and both exist because a use case puts several org-unit layers on one
 * geography at once:
 *
 * * a one-unit `outlineOnly` highlight is skipped (`SELECTED_HIGHLIGHT`) — it is a
 *   selection marker, not a map of the level;
 * * of what is left, the layer naming the **most** units wins. UC2's land-cover chip
 *   registers a hairline boundary layer over every watershed beside a narrower pattern
 *   choropleth, and the names belong to whichever of the two actually covers the map.
 *
 * `null` when no candidate qualifies, which is what puts the names back on the §F5.8 rows.
 */
export function labelOwner<T extends LabelCandidate>(results: readonly T[]): T | null {
  let best: T | null = null;
  let bestCount = -1;
  for (const result of results) {
    const { spec } = result;
    if (spec.kind !== 'units' && spec.kind !== 'choropleth') continue;
    const count = labelUnitCount(spec);
    if (spec.outlineOnly && count <= 1 && SELECTED_HIGHLIGHT.test(result.id)) continue;
    if (count > bestCount) {
      best = result;
      bestCount = count;
    }
  }
  return best;
}

/**
 * The level a §F6 result layer is drawing org units at right now, or `null` when none is —
 * the level whose hierarchy labels must therefore stand down.
 *
 * A result's `units`/`choropleth` geometry is the *explorer's own* polygons at the current
 * `reportingLevel` (`resultUnits.ts::useResultUnits`), so a UC1 priority map and the
 * ticked Watersheds row draw the very same shapes. Labelling both would write every name
 * twice, a pixel apart. The result wins because it is the thing the reader asked for, and
 * because it paints only the units it actually names — a label beside an unpainted
 * neighbour would claim the result covers it.
 *
 * Non-null only when `labelOwner` finds one, so a lone `intel:{uc}:selected` highlight
 * leaves the hierarchy row labelling: the one thing worse than two labels on a watershed
 * is no label on any of the other ten.
 */
export function resultLabelLevel(
  results: readonly LabelCandidate[],
  reportingLevel: ReportingLevel,
): ReportingLevel | null {
  return labelOwner(results) ? reportingLevel : null;
}
